import type { FieldError, IdeaStatus, PlatformSettingsPatch, Role, UserStatus } from './types';
import { ROLES } from './types';
import { isIdeaStatus } from './ideaStatus';

/**
 * 表单校验 —— 前后端共用。
 *
 * 所有校验器都返回 `{ errors, value }`：`value` 永远是**归一化后的完整结果**，
 * 因此调用方可以在 `errors` 为空时直接把 `value` 落库，不需要再写一遍 trim / 默认值。
 */

export const IDEA_LIMITS = {
  titleMin: 4,
  titleMax: 80,
  summaryMin: 8,
  summaryMax: 160,
  bodyMin: 20,
  bodyMax: 8000,
  hypothesisMin: 8,
  hypothesisMax: 500,
  audienceMin: 2,
  audienceMax: 120,
  successMetricMin: 2,
  successMetricMax: 300,
  commentMin: 1,
  commentMax: 2000,
  tagsMax: 5,
  tagMax: 24,
  capabilityMax: 20000,
  tagCatalogMax: 30,
  aiDailyLimitMax: 200,
} as const;

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;
export const PASSWORD_HINT = `至少 ${PASSWORD_MIN_LENGTH} 位，且同时包含字母与数字`;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function readString(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

/* ============================================================ 基础字段 */

export function validateEmail(value: unknown): FieldError | null {
  const email = readString(value).toLowerCase();
  if (email === '') return { field: 'email', message: '请填写邮箱' };
  if (email.length > 160) return { field: 'email', message: '邮箱过长' };
  if (!EMAIL_PATTERN.test(email)) return { field: 'email', message: '邮箱格式不正确' };
  return null;
}

export function normalizeEmail(value: unknown): string {
  return readString(value).toLowerCase();
}

export function validatePassword(value: unknown): FieldError | null {
  const password = typeof value === 'string' ? value : '';
  if (password === '') return { field: 'password', message: '请填写口令' };
  if (password.length < PASSWORD_MIN_LENGTH) return { field: 'password', message: `口令至少 ${PASSWORD_MIN_LENGTH} 位` };
  if (password.length > PASSWORD_MAX_LENGTH) return { field: 'password', message: '口令过长' };
  if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
    return { field: 'password', message: '口令需同时包含字母与数字' };
  }
  return null;
}

export function validateName(value: unknown): FieldError | null {
  const name = readString(value);
  if (name === '') return { field: 'name', message: '请填写姓名' };
  if (name.length > 40) return { field: 'name', message: '姓名不能超过 40 个字符' };
  return null;
}

export function isRoleValue(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

/* ============================================================ 标签 */

/** 归一化标签：去空、去重（忽略大小写）、按上限截断。非字符串一律丢弃。 */
export function normalizeTags(value: unknown, max: number = IDEA_LIMITS.tagsMax): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of value) {
    if (typeof raw !== 'string') continue;
    const tag = raw.trim().replace(/^#/, '');
    if (tag === '' || tag.length > IDEA_LIMITS.tagMax) continue;
    const key = tag.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(tag);
    if (result.length >= max) break;
  }
  return result;
}

/* ============================================================ 创意 */

export interface IdeaInput {
  title: string;
  summary: string;
  body: string;
  hypothesis: string;
  audience: string;
  successMetric: string;
  tags: string[];
  status: Extract<IdeaStatus, 'draft' | 'submitted'>;
}

function checkLength(errors: FieldError[], field: string, label: string, value: string, min: number, max: number): void {
  if (value.length < min) {
    errors.push({ field, message: `${label}至少 ${min} 个字符` });
    return;
  }
  if (value.length > max) {
    errors.push({ field, message: `${label}不能超过 ${max} 个字符` });
  }
}

/**
 * 校验创建创意的入参。
 *
 * `draft` 允许信息不全（成员可以先记下来），`submitted` 必须把研判需要的字段填满 ——
 * 少填字段的代价是 AI 只能给 `needs_more_info`，与其事后补，不如提交时挡住。
 */
export function validateIdeaInput(raw: Record<string, unknown>): { errors: FieldError[]; value: IdeaInput } {
  const errors: FieldError[] = [];

  const statusValue = readString(raw['status']).toLowerCase();
  const status: IdeaInput['status'] = statusValue === 'draft' ? 'draft' : 'submitted';

  const title = readString(raw['title']);
  const summary = readString(raw['summary']);
  const body = readString(raw['body']);
  const hypothesis = readString(raw['hypothesis']);
  const audience = readString(raw['audience']);
  const successMetric = readString(raw['successMetric']);
  const tags = normalizeTags(raw['tags']);

  if (status === 'draft') {
    if (title === '') errors.push({ field: 'title', message: '草稿也需要一个标题' });
    else checkLength(errors, 'title', '标题', title, IDEA_LIMITS.titleMin, IDEA_LIMITS.titleMax);
  } else {
    checkLength(errors, 'title', '标题', title, IDEA_LIMITS.titleMin, IDEA_LIMITS.titleMax);
    checkLength(errors, 'summary', '一句话说明', summary, IDEA_LIMITS.summaryMin, IDEA_LIMITS.summaryMax);
    checkLength(errors, 'body', '详细说明', body, IDEA_LIMITS.bodyMin, IDEA_LIMITS.bodyMax);
    checkLength(errors, 'hypothesis', '核心假设', hypothesis, IDEA_LIMITS.hypothesisMin, IDEA_LIMITS.hypothesisMax);
    checkLength(errors, 'audience', '面向对象', audience, IDEA_LIMITS.audienceMin, IDEA_LIMITS.audienceMax);
    checkLength(errors, 'successMetric', '成功指标', successMetric, IDEA_LIMITS.successMetricMin, IDEA_LIMITS.successMetricMax);
  }

  // 草稿也限制长度，避免把整份文档粘进来
  if (title.length > IDEA_LIMITS.titleMax) errors.push({ field: 'title', message: `标题不能超过 ${IDEA_LIMITS.titleMax} 个字符` });
  if (summary.length > IDEA_LIMITS.summaryMax) errors.push({ field: 'summary', message: `一句话说明不能超过 ${IDEA_LIMITS.summaryMax} 个字符` });
  if (body.length > IDEA_LIMITS.bodyMax) errors.push({ field: 'body', message: `详细说明不能超过 ${IDEA_LIMITS.bodyMax} 个字符` });
  if (hypothesis.length > IDEA_LIMITS.hypothesisMax) errors.push({ field: 'hypothesis', message: `核心假设不能超过 ${IDEA_LIMITS.hypothesisMax} 个字符` });
  if (audience.length > IDEA_LIMITS.audienceMax) errors.push({ field: 'audience', message: `面向对象不能超过 ${IDEA_LIMITS.audienceMax} 个字符` });
  if (successMetric.length > IDEA_LIMITS.successMetricMax) errors.push({ field: 'successMetric', message: `成功指标不能超过 ${IDEA_LIMITS.successMetricMax} 个字符` });
  if (Array.isArray(raw['tags']) && normalizeTags(raw['tags'], IDEA_LIMITS.tagsMax + 1).length > IDEA_LIMITS.tagsMax) {
    errors.push({ field: 'tags', message: `最多选择 ${IDEA_LIMITS.tagsMax} 个标签` });
  }

  return { errors, value: { title, summary, body, hypothesis, audience, successMetric, tags, status } };
}

export interface IdeaPatch {
  title?: string;
  summary?: string;
  body?: string;
  hypothesis?: string;
  audience?: string;
  successMetric?: string;
  tags?: string[];
}

/** 校验创意的部分更新：只归一化出现过的字段。 */
export function validateIdeaPatch(raw: Record<string, unknown>): { errors: FieldError[]; value: IdeaPatch } {
  const errors: FieldError[] = [];
  const value: IdeaPatch = {};

  const textFields: { key: 'title' | 'summary' | 'body' | 'hypothesis' | 'audience' | 'successMetric'; label: string; max: number }[] = [
    { key: 'title', label: '标题', max: IDEA_LIMITS.titleMax },
    { key: 'summary', label: '一句话说明', max: IDEA_LIMITS.summaryMax },
    { key: 'body', label: '详细说明', max: IDEA_LIMITS.bodyMax },
    { key: 'hypothesis', label: '核心假设', max: IDEA_LIMITS.hypothesisMax },
    { key: 'audience', label: '面向对象', max: IDEA_LIMITS.audienceMax },
    { key: 'successMetric', label: '成功指标', max: IDEA_LIMITS.successMetricMax },
  ];

  for (const field of textFields) {
    if (!(field.key in raw)) continue;
    const text = readString(raw[field.key]);
    if (text === '') {
      errors.push({ field: field.key, message: `${field.label}不能为空` });
      continue;
    }
    if (text.length > field.max) {
      errors.push({ field: field.key, message: `${field.label}不能超过 ${field.max} 个字符` });
      continue;
    }
    value[field.key] = text;
  }

  if ('tags' in raw) {
    const tags = normalizeTags(raw['tags']);
    if (Array.isArray(raw['tags']) && normalizeTags(raw['tags'], IDEA_LIMITS.tagsMax + 1).length > IDEA_LIMITS.tagsMax) {
      errors.push({ field: 'tags', message: `最多选择 ${IDEA_LIMITS.tagsMax} 个标签` });
    } else {
      value.tags = tags;
    }
  }

  return { errors, value };
}

export function validateCommentBody(value: unknown): FieldError | null {
  const body = readString(value);
  if (body === '') return { field: 'body', message: '评论内容不能为空' };
  if (body.length > IDEA_LIMITS.commentMax) return { field: 'body', message: `评论不能超过 ${IDEA_LIMITS.commentMax} 个字符` };
  return null;
}

export function normalizeCommentBody(value: unknown): string {
  return readString(value);
}

/** 归一化目标时间：接受 ISO 字符串或 `YYYY-MM-DD`，返回 ISO UTC 或 null。 */
export function normalizeDueAt(value: unknown): { ok: boolean; value: string | null } {
  if (value === null || value === undefined || value === '') return { ok: true, value: null };
  const text = readString(value);
  const parsed = new Date(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T23:59:59.000Z` : text);
  if (Number.isNaN(parsed.getTime())) return { ok: false, value: null };
  return { ok: true, value: parsed.toISOString() };
}

export function validateIdeaStatus(value: unknown): FieldError | null {
  if (!isIdeaStatus(value)) return { field: 'status', message: '状态不合法' };
  return null;
}

/* ============================================================ 用户 */

export interface UserInput {
  email: string;
  name: string;
  role: Role;
  status: UserStatus;
  password: string;
}

/** 校验建号入参；口令必填（沿用管理员建号 + 首次登录强制改口令的方式）。 */
export function validateUserCreateInput(raw: Record<string, unknown>): { errors: FieldError[]; value: UserInput } {
  const errors: FieldError[] = [];

  const emailError = validateEmail(raw['email']);
  if (emailError) errors.push(emailError);

  const nameError = validateName(raw['name']);
  if (nameError) errors.push(nameError);

  const roleValue = readString(raw['role']);
  const role: Role = isRoleValue(roleValue) ? roleValue : 'member';
  if (roleValue !== '' && !isRoleValue(roleValue)) errors.push({ field: 'role', message: '角色不合法' });

  const passwordError = validatePassword(raw['password']);
  if (passwordError) errors.push(passwordError);

  return {
    errors,
    value: {
      email: normalizeEmail(raw['email']),
      name: readString(raw['name']),
      role,
      status: 'active',
      password: typeof raw['password'] === 'string' ? raw['password'] : '',
    },
  };
}

export interface UserPatch {
  name?: string;
  role?: Role;
  status?: UserStatus;
}

export function validateUserPatch(raw: Record<string, unknown>): { errors: FieldError[]; value: UserPatch } {
  const errors: FieldError[] = [];
  const value: UserPatch = {};

  if ('name' in raw) {
    const nameError = validateName(raw['name']);
    if (nameError) errors.push(nameError);
    else value.name = readString(raw['name']);
  }

  if ('role' in raw) {
    const roleValue = readString(raw['role']);
    if (!isRoleValue(roleValue)) errors.push({ field: 'role', message: '角色不合法' });
    else value.role = roleValue;
  }

  if ('status' in raw) {
    const statusValue = readString(raw['status']);
    if (statusValue !== 'active' && statusValue !== 'suspended') {
      errors.push({ field: 'status', message: '状态不合法' });
    } else {
      value.status = statusValue;
    }
  }

  return { errors, value };
}

/* ============================================================ 平台设置 */

export function validateSettingsPatch(raw: Record<string, unknown>): { errors: FieldError[]; value: PlatformSettingsPatch } {
  const errors: FieldError[] = [];
  const value: PlatformSettingsPatch = {};

  if ('aiEnabled' in raw) {
    if (typeof raw['aiEnabled'] !== 'boolean') errors.push({ field: 'aiEnabled', message: '开关值不合法' });
    else value.aiEnabled = raw['aiEnabled'];
  }

  if ('aiDailyLimit' in raw) {
    const limit = Number(raw['aiDailyLimit']);
    if (!Number.isInteger(limit) || limit < 0 || limit > IDEA_LIMITS.aiDailyLimitMax) {
      errors.push({ field: 'aiDailyLimit', message: `每人每日额度需为 0–${IDEA_LIMITS.aiDailyLimitMax} 之间的整数，0 表示不限` });
    } else {
      value.aiDailyLimit = limit;
    }
  }

  if ('aiModel' in raw) {
    const model = readString(raw['aiModel']);
    if (model === '') errors.push({ field: 'aiModel', message: '请填写模型名' });
    else if (model.length > 120) errors.push({ field: 'aiModel', message: '模型名过长' });
    else value.aiModel = model;
  }

  if ('tags' in raw) {
    if (!Array.isArray(raw['tags'])) {
      errors.push({ field: 'tags', message: '标签需为数组' });
    } else {
      const tags = normalizeTags(raw['tags'], IDEA_LIMITS.tagCatalogMax);
      if (tags.length !== raw['tags'].length) {
        errors.push({ field: 'tags', message: `标签需去重，单个不超过 ${IDEA_LIMITS.tagMax} 个字符，最多 ${IDEA_LIMITS.tagCatalogMax} 个` });
      } else {
        value.tags = tags;
      }
    }
  }

  if ('capabilities' in raw) {
    const capabilities = typeof raw['capabilities'] === 'string' ? raw['capabilities'].trim() : '';
    if (capabilities.length > IDEA_LIMITS.capabilityMax) {
      errors.push({ field: 'capabilities', message: `能力清单不能超过 ${IDEA_LIMITS.capabilityMax} 个字符` });
    } else {
      value.capabilities = capabilities;
    }
  }

  return { errors, value };
}

/** 默认设置。平台首次启动、以及 settings 行缺失时使用。 */
export const DEFAULT_PLATFORM_TAGS: readonly string[] = ['内部提效', '客户价值', '成本优化', '数据与算法', '体验优化'];

export const DEFAULT_CAPABILITIES = [
  '现有系统：',
  '- Havorix 无人机运营监管平台（havorix.work）：Cloudflare Workers + D1 后端，React 19 + Vite + Tailwind v4 前端，',
  '  已具备多机管理、实时遥测、告警阈值、RBAC 权限与审计日志。',
  '- Havorix 品牌官网（havorix.cn）：静态站点，自托管字体与设计令牌。',
  '',
  '技术栈：',
  '- 后端：Cloudflare Workers（模块化 Worker 入口 + 手写路由表）、D1（SQLite）、Pages。',
  '- 前端：React 19、Vite、Tailwind CSS v4（CSS-first 令牌）、react-router-dom、zustand。',
  '- 认证：自建邮箱 + 口令 + HttpOnly Cookie 会话（HS256 JWT + PBKDF2-SHA256）。',
  '- 测试：Vitest，覆盖共享契约与纯逻辑。',
  '',
  '可复用资产：',
  '- 语义化设计令牌与组件库（按钮、卡片、表格、弹层、Toast）。',
  '- Worker 侧的鉴权守卫（requirePermission）、审计写入、统一错误报文。',
  '- 交接物：员工平台（本平台）与无人机平台共用同一套工程约定。',
].join('\n');
